import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BirthData } from '../types';
import { parse } from 'date-fns';
import { calculateAllPositions } from '../utils/astrologyCalculations';
import Chart, { Settings } from '@astrodraw/astrochart';
import { useTranslation } from 'react-i18next';

interface AstralChartProps {
  birthData: BirthData;
  type: string;
}

interface PlanetaryPosition {
  sign: string;
  degrees: number;
  minutes?: number;
}

interface HousePosition {
  sign: string;
  degrees: number;
}

interface ChartData {
  [key: string]: PlanetaryPosition | { [key: number]: HousePosition } | PlanetaryPosition;
  houses: { [key: number]: HousePosition };
  ascendant: PlanetaryPosition;
}

const AstralChart: React.FC<AstralChartProps> = ({ birthData, type }) => {

  const { t } = useTranslation();

  const [chartData, setChartData] = useState<any | null>(null);

  const chartRef = useRef<Chart | null>(null);

  const generateChart = useCallback(() => {

    console.log("birthData", birthData);
    const date = parse(`${birthData.birthDate} ${birthData.birthTime}`, 'yyyy-MM-dd HH:mm', new Date());
    const [hours, minutes] = birthData.birthTime.split(':').map(Number);

    console.log("generateChart", date, hours, minutes);

    const positions = calculateAllPositions({
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate(),
      hour: hours,
      minute: minutes,
      latitude: birthData.latitude || 0,
      longitude: birthData.longitude || 0
    });

    console.log(positions);

    setChartData(positions as ChartData);

    // expose the chart data to the window object
    (window as any).astralCalculations = positions;

  }, [birthData]);

  useEffect(() => {
    generateChart();
  }, [generateChart]);

  useEffect(() => {
    if (chartData) {
      const ORIGIN_SHIFT = chartData.celestial_ascendant.ChartPosition.Ecliptic.DecimalDegrees;
      const ASCENDANT_SHIFT = ORIGIN_SHIFT + 180;
      const PLANTES_SHIFT = ASCENDANT_SHIFT + 180;

      const settings: Partial<Settings> = {
        SYMBOL_SCALE: 1.4,
        SYMBOL_AXIS_STROKE: 1.2,
        SHIFT_IN_DEGREES: ASCENDANT_SHIFT,
        SYMBOL_AXIS_FONT_COLOR: '#333',
        INDOOR_CIRCLE_RADIUS_RATIO: 2.2,
        COLORS_SIGNS: ["#6a32e6", "#7a32e6ee", "#6a32e6", "#7a32e6ee", "#6a32e6", "#7a32e6ee", "#6a32e6", "#7a32e6ee", "#6a32e6", "#7a32e6ee", "#6a32e6", "#7a32e6ee"],
        LINE_COLOR: "#777",
        CUSPS_FONT_COLOR: "#000",
        SIGNS_COLOR: "#fff",
        CIRCLE_STRONG: 1.2,
        POINTS_COLOR: "#333",
        // CUSPS_STROKE: 1.2,
        RULER_RADIUS: 14,
        SYMBOL_CUSP_1: "1",
        SYMBOL_CUSP_2: "2",
        SYMBOL_CUSP_3: "3",
        SYMBOL_CUSP_4: "4",
        SYMBOL_CUSP_5: "5",
        SYMBOL_CUSP_6: "6",
        SYMBOL_CUSP_7: "7",
        SYMBOL_CUSP_8: "8",
        SYMBOL_CUSP_9: "9",
        SYMBOL_CUSP_10: "10",
        SYMBOL_CUSP_11: "11",
        SYMBOL_CUSP_12: "12",
      };

      const CHART_SIZE = 750;

      const planets = chartData.celestial_bodies.all.reduce((acc: any, planet: any) => {
        if (planet.key === "sirius") return acc;
        acc[planet.label] = [planet.ChartPosition.Ecliptic.DecimalDegrees + PLANTES_SHIFT]
        return acc;
      }, {});

      planets['Lilith'] = [chartData.celestial_points.lilith.ChartPosition.Ecliptic.DecimalDegrees + PLANTES_SHIFT];
      planets['NNode'] = [chartData.celestial_points.northnode.ChartPosition.Ecliptic.DecimalDegrees + PLANTES_SHIFT];

      // planets['Ascendant'] = [chartData.celestial_ascendant.ChartPosition.Ecliptic.DecimalDegrees + PLANTES_SHIFT];

      const cusps = chartData.cusps.map((cusp: any) => {
        return cusp.ChartPosition.StartPosition.Ecliptic.DecimalDegrees
      });
      

      const data = {
        planets: planets,
        cusps: cusps
      }


      let chart: Chart;
      let radix: any;
      

      if (!chartRef.current) {
        chart = new Chart('astral-chart', CHART_SIZE, CHART_SIZE, settings);
        chartRef.current = chart;
      }
      else {
        chart = chartRef.current;
      }
      
      radix = chart.radix(data);

      if (radix) {
        radix.aspects();
        radix.shift = 0;
        radix?.drawRuler();
        radix?.drawAxis();
        radix.drawCusps();
        radix.drawCircles();
        radix?.drawUniverse();
        
        // console.log(radix.paper.context);

        // Add zodiac degree indicators to sign groups
        chartData.cusps.forEach((planet: any, index: number) => {
          if (planet.Sign) {
            const signGroupId = `astral-chart-astrology-radix-signs-${planet.Sign.label}`;
            const signGroup = document.getElementById(signGroupId);
            const circleGroup = document.getElementById(`astral-chart-astrology-radix-circles`);

            if (signGroup && circleGroup) {
              // Remove existing text elements if they exist
              // const existingStartText = document.getElementById(`${signGroupId}-start-text`);
              // const existingEndText = document.getElementById(`${signGroupId}-end-text`);
              // if (existingStartText) existingStartText.remove();
              // if (existingEndText) existingEndText.remove();

              // Get the path element and its bounding box
              const path = Array.from(circleGroup.querySelectorAll('circle')).sort((a, b) => {
                const rA = parseFloat(a.getAttribute('r') || '0');
                const rB = parseFloat(b.getAttribute('r') || '0');
                return rA > rB ? -1 : 1;
              })[0];

              if (path) {
                const bbox = path.getBBox();
                let centerX = bbox.x + bbox.width / 2;
                let centerY = bbox.y + bbox.height / 2;

                const arcDegrees = planet.ChartPosition.StartPosition.Ecliptic.ArcDegreesFormatted30;

                const formatArcDegrees = (arcDegrees: string): string[] => {
                  const [degrees, minutes] = arcDegrees.split(" ").map((part: string) =>
                    part.replace(/[^0-9]/g, "")
                  );
                  const roundedMinutes = parseInt(minutes);
                  return [`${degrees}°`, `${roundedMinutes}'`];
                };

                const arcDegreesRounded = formatArcDegrees(arcDegrees);

                const startDegree = arcDegreesRounded[0];
                const endDegree = arcDegreesRounded[1];

                let startX = centerX;
                let startY = centerY;
                let endX = centerX;
                let endY = centerY;

                // position along the circle, considering its radius
                const circleParts = 12;
                const part = index % circleParts;
                const angle = (part / circleParts) * -360 + ORIGIN_SHIFT + (360 / (circleParts * 2));

                let startAngle = angle + 5;
                let endAngle = angle - 5;

                if (angle > 0 || angle < -180) {
                  startAngle = angle + 5;
                  endAngle = angle - 5;
                }
                else {
                  startAngle = angle - 5;
                  endAngle = angle + 5;
                }

                const radius = parseFloat(path.getAttribute('r') || '0') - 20;

                const x = Math.cos(startAngle * (Math.PI / 180)) * (radius);
                const y = Math.sin(startAngle * (Math.PI / 180)) * (radius);

                const x2 = Math.cos(endAngle * (Math.PI / 180)) * (radius);
                const y2 = Math.sin(endAngle * (Math.PI / 180)) * (radius);

                startX = centerX + x;
                startY = centerY + y + 5;
                endX = centerX + x2;
                endY = centerY + y2 + 5;

                console.log(angle, startDegree, endDegree);

                // Create start degree text
                const startText = document.createElementNS("http://www.w3.org/2000/svg", "text");
                startText.id = `${signGroupId}-start-text`;

                startText.setAttribute("x", startX.toString());
                startText.setAttribute("y", startY.toString());
                startText.setAttribute("text-anchor", "middle");
                startText.setAttribute("fill", "#FFFFFF");
                startText.setAttribute("font-size", "14");
                startText.setAttribute("z-index", "100");
                startText.textContent = `${startDegree}`;

                // Create end degree text
                const endText = document.createElementNS("http://www.w3.org/2000/svg", "text");
                endText.id = `${signGroupId}-end-text`;
                endText.setAttribute("x", endX.toString());
                endText.setAttribute("y", endY.toString());
                endText.setAttribute("text-anchor", "middle");
                endText.setAttribute("fill", "#FFFFFF");
                endText.setAttribute("font-size", "14");
                endText.setAttribute("z-index", "100");
                endText.textContent = `${endDegree}`;

                // group the texts
                const textGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
                textGroup.id = `${signGroupId}-text-group`;
                textGroup.appendChild(startText);
                textGroup.appendChild(endText);

                circleGroup.appendChild(textGroup);
              }
            }
          }
        });

        // Signal that the chart is ready for PDF generation
        setTimeout(() => {

          // Add a visible element for Puppeteer to wait for
          const readyElement = document.createElement('div');
          readyElement.id = 'chart-ready';
          document.body.appendChild(readyElement);

          // Dispatch custom event for Puppeteer to listen to
          window.dispatchEvent(new CustomEvent('astralChartReady', {
            detail: {
              chartData: chartData,
              timestamp: new Date().toISOString()
            }
          }));
          console.log('AstralChart ready for PDF generation');

          // Also dispatch a second event after a short delay to ensure it's received
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('astralChartReady', {
              detail: {
                chartData: chartData,
                timestamp: new Date().toISOString(),
                retry: true
              }
            }));
            console.log('AstralChart ready event retry sent');
          }, 1000);
        }, 3000); // Increased to 3 seconds for more stability
      }
    }
  }, [chartData]);

  if (!chartData) {
    return <div className="text-center p-4">Calculando seu mapa astral...</div>;
  }

  return (
    <div>
      <div id="map-container" className='w-full h-[48vh] p-4 flex flex-col items-center justify-center'>
        <div id="astral-chart"></div>
      </div>
      <div className='w-full flex flex-col items-center justify-center'>

        <div className='max-w-3xl mx-auto flex flex-wrap justify-center'>
          {chartData?.celestial_bodies.all.map((item: any) => {
            if (item.key === "sirius") return null;
            return (
              <div key={item.label} className='basis-1/3 p-1 bg-gray-50 rounded-lg leading-none border border-white'>
                <h4 className='font-bold text-purple-800'>{t(`chart.${item.key}`)} - {t(`signs.${item.Sign.key.toLowerCase()}`)} {item.isRetrograde ? <span className='text-xs font-normal'> (Retrogrado)</span> : ''}</h4>
                <p className='text-sm'>{item.ChartPosition.Ecliptic.ArcDegreesFormatted30} - {t(`houses.${item.House.label}`)}</p>
              </div>
            )
          })}
          {[chartData?.celestial_ascendant].map((item: any) => {
            if (item.key === "sirius") return null;
            return (
              <div key={item.label} className='basis-1/3 p-1 bg-gray-50 rounded-lg leading-none border border-white'>
                <h4 className='font-bold text-purple-800'>{t(`chart.${item.key}`)} - {t(`signs.${item.Sign.key.toLowerCase()}`)} {item.isRetrograde ? <span className='text-xs font-normal'> (Retrogrado)</span> : ''}</h4>
                <p className='text-sm'>{item.ChartPosition.Ecliptic.ArcDegreesFormatted30}</p>
              </div>
            )
          })}
          {type === "tikkun-map" && chartData?.celestial_points.all.map((item: any) => {

            if (item.key === "southnode") return null;

            return (
              <div key={item.label} className='basis-1/3 p-1 bg-gray-50 rounded-lg leading-none border border-white'>
                <h4 className='font-bold text-purple-800'>{t(item.label)} - {t(`signs.${item.Sign.key.toLowerCase()}`)} {item.isRetrograde ? <span className='text-xs font-normal'> (Retrogrado)</span> : ''}</h4>
                <p className='text-sm'>{item.ChartPosition.Ecliptic.ArcDegreesFormatted30} - {t(`houses.${item.House.label}`)}</p>
              </div>
            )
          })}
        </div>

      </div>
    </div>
  );
};

export default AstralChart; 